/**
 * Did this outside-press start on an element that has since left the document?
 *
 * **Why an overlay has to ask.** Radix's `DismissableLayer` does not act on an
 * outside `pointerdown` when the pointer is a finger. It records it, registers
 * a one-shot `click` listener, and decides there instead — deliberately, so a
 * touch does not dismiss twice. But the decision it makes at `click` reads the
 * layer stack as it stands THEN, and the whole point of a `click` is that a
 * React handler has already run: a modal stacked over this layer can have
 * closed in between. This layer, which was not the top layer when the finger
 * went down, finds itself top by the time Radix asks, and dismisses for a press
 * that was never aimed at it.
 *
 * Without this, tapping `Acknowledged` on the Scan & Go heavy-lift caution
 * would close the caution and then the scanner under it, putting the customer
 * back at the order form — every time, forever, on a phone and only on a phone.
 * A mouse never reaches the branch at all: `pointerdown` with
 * `pointerType: 'mouse'` is dispatched immediately, while the layer above is
 * still mounted and still holds the pointer.
 *
 * **The trace the unmount leaves is the only thing left to read.** By the time
 * Radix asks, the layer above is gone from its own stack, and gone from
 * `context.branches` too — so neither `isPointerEventsEnabled` nor a
 * `DismissableLayer.Branch` can answer, which is why this is not solved by
 * Radix's own escape hatch. What survives is the press's origin: a control that
 * closed the thing it was inside is not in the document, while a backdrop that
 * was genuinely pressed still is. So the rule an overlay needs is exactly this
 * one — **dismiss for a press whose origin still exists, and ignore the tail of
 * a press whose origin has been torn out from under it.**
 *
 * No timer and no grace window: the question is about the DOM, not about how
 * long ago something happened, and it answers the same way on a 40ms tap and a
 * 4-second press. It is a no-op under a mouse, where the dispatch is synchronous
 * and nothing can have unmounted yet.
 *
 * **Only a layer stacked as a React SIBLING can do this.** Radix marks a press
 * that starts anywhere in a layer's React tree — portals included — as inside
 * it before deferring anything, so a menu, select or dialog rendered within
 * this layer's children never reaches the branch. The sibling stacks behind each
 * caller, every one pinned by a touch case that is red without this guard: the
 * lift caution over the Scan & Go `Sheet`, and the app-root `ConfirmProvider`
 * over a `Dialog`, a `Drawer` or the entity preview. An overlay nothing is ever
 * stacked beside does not need it.
 */
export function pressOriginIsDetached(
  event: CustomEvent<{ originalEvent: Event }>,
): boolean {
  const origin = event.detail.originalEvent.target
  // Anything that is not an element — a press reported against the document
  // itself, or a target the browser has already dropped — is left to the
  // layer's own rules rather than guessed at.
  return origin instanceof Node && !origin.isConnected
}

/**
 * Is this Escape meant for an open list inside the overlay, not the overlay?
 *
 * Radix listens for Escape on the document in the CAPTURE phase, so the overlay
 * decides before the focused input's own handler runs — and a combobox whose
 * list is not a Radix layer of its own (`AddressAutocomplete`) has no way to
 * stop it. Pressing Escape to close the suggestions therefore closed the whole
 * dialog and threw away the form around it. The combobox states its openness in
 * `aria-expanded`, still true at this point, so that is what is read: the
 * overlay stays, and the combobox closes its own list.
 */
export function escapeBelongsToOpenList(event: KeyboardEvent): boolean {
  const target = event.target
  return (
    target instanceof Element &&
    target.closest('[role="combobox"][aria-expanded="true"]') !== null
  )
}
